import { dropTask } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import * as v from 'valibot';
import Component from '@glimmer/component';
import { Form, type FormResultData } from 'tio-ui/components/forms';
import { Button } from 'tio-ui/components/buttons';
import { VStack } from 'tio-ui/components/layout';
import type PostLoginService from 'tio-employee/services/post-login';
import type SessionService from 'tio-employee/services/session';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';

const formSchema = v.object({
  code: v.pipe(v.string(), v.nonEmpty('Code is required'), v.minLength(6, 'Enter 6 digits')),
});

type MfaFormData = v.InferOutput<typeof formSchema>;

export default class PostLoginMfaSmsTemplate extends Component {
  @service declare postLogin: PostLoginService;
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare intl: IntlService;

  @tracked formData: MfaFormData = { code: '' };
  @tracked errorMessage = '';
  @tracked resendMessage = '';

  handleFormChange = (result: FormResultData<MfaFormData>) => {
    this.formData = result.data;
  };

  submitCode = dropTask(async (result: FormResultData<MfaFormData>) => {
    this.errorMessage = '';
    this.resendMessage = '';
    try {
      const accessToken = await this.postLogin.verifyMfa(result.data.code);
      await this.session.authenticate('authenticator:saml', { access_token: accessToken });
      this.postLogin.clear();
      this.session.renew();
      this.router.transitionTo('authenticated');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : this.intl.t('login.mfa_sms.error_unknown');
      this.errorMessage = message;
    }
  });

  resendCode = dropTask(async () => {
    this.errorMessage = '';
    this.resendMessage = '';
    try {
      await this.postLogin.resendMfa();
      this.resendMessage = this.intl.t('login.mfa_sms.resend_success');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : this.intl.t('login.mfa_sms.error_unknown');
      this.errorMessage = message;
    }
  });

  <template>
    <div class="min-h-screen bg-gray-50 flex items-center justify-center">
      <div class="w-full max-w-md bg-white rounded-lg shadow p-8">
        <VStack class="gap-y-6">
          <div class="text-center">
            <h1 class="text-2xl font-semibold">{{t "login.mfa_sms.title"}}</h1>
            <p class="text-gray-600 mt-2">{{t "login.mfa_sms.description"}}</p>
          </div>

          <Form
            @data={{this.formData}}
            @schema={{formSchema}}
            @onChange={{this.handleFormChange}}
            @onSubmit={{this.submitCode.perform}}
            as |form|
          >
            <form.Field @name="code" as |field|>
              <field.Input
                @label={{t "login.mfa_sms.code_label"}}
                @type="text"
                data-test-mfa-code-input
              />
            </form.Field>

            <div class="flex justify-between items-center mt-2">
              <Button
                @intent="default"
                @appearance="outlined"
                @isRunning={{this.resendCode.isRunning}}
                {{on "click" this.resendCode.perform}}
                aria-label={{t "login.mfa_sms.resend"}}
                data-test-mfa-resend
              >
                {{t "login.mfa_sms.resend"}}
              </Button>
              <Button
                @type="submit"
                @intent="primary"
                @isRunning={{this.submitCode.isRunning}}
                aria-label={{t "login.mfa_sms.submit"}}
                data-test-mfa-submit
              >
                {{t "login.mfa_sms.submit"}}
              </Button>
            </div>
          </Form>

          {{#if this.resendMessage}}
            <p
              class="text-green-600 text-sm"
              data-test-mfa-resend-success
            >{{this.resendMessage}}</p>
          {{/if}}

          {{#if this.errorMessage}}
            <p class="text-red-600 text-sm" data-test-mfa-error>{{this.errorMessage}}</p>
          {{/if}}
        </VStack>
      </div>
    </div>
  </template>
}
